import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { client } from "@/apis/gql/apolloClient";
import { useTenant } from "@/providers/session";

import { GET_ALL_WHATSAPP_FORWARDINGS } from "./get-all";

type UpdateWhatsAppForwardingInput = {
  whatsappForwardingId: string;
  phoneNumber?: string;
  name?: string;
  onboardingState?: string;
};

export const useUpdateWhatsAppForwarding = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [updateWhatsAppForwarding, { data, loading, error }] = useMutation(
    UPDATE_WHATSAPP_FORWARDING_MUTATION,
    {
      optimisticResponse: (vars: any) => {
        const cache = client.cache;
        const existingForwarding = cache.readFragment({
          id: `WhatsAppForwarding:${vars.whatsappForwardingId}`,
          fragment: gql`
            fragment ExistingWhatsAppForwarding on WhatsAppForwarding {
              whatsappForwardingId
              phoneNumber
              name
              onboardingState
              createdAt
              updatedAt
            }
          `,
        });

        if (!existingForwarding) {
          return {
            updateWhatsAppForwarding: null,
          };
        }

        const updates = {
          phoneNumber: vars.updatedFields.phoneNumber,
          name: vars.updatedFields.name,
          onboardingState: vars.updatedFields.onboardingState,
        };

        return {
          updateWhatsAppForwarding: {
            __typename: "WhatsAppForwarding",
            ...existingForwarding,
            ...updates,
          },
        };
      },
      update: (cache, { data: { updateWhatsAppForwarding } }) => {
        if (updateWhatsAppForwarding) {
          cache.modify({
            id: cache.identify(updateWhatsAppForwarding),
            fields: {
              phoneNumber: () => updateWhatsAppForwarding.phoneNumber,
              name: () => updateWhatsAppForwarding.name,
              onboardingState: () => updateWhatsAppForwarding.onboardingState,
            },
          });
        }
      },
      refetchQueries: [GET_ALL_WHATSAPP_FORWARDINGS],
    },
  );

  const handleUpdateWhatsAppForwarding = async (
    data: UpdateWhatsAppForwardingInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateWhatsAppForwarding({
        variables: {
          whatsappForwardingId: data.whatsappForwardingId,
          updatedFields: {
            phoneNumber: data.phoneNumber,
            name: data.name,
            onboardingState: data.onboardingState,
          },
          tenantId,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return {
    updateWhatsAppForwarding: handleUpdateWhatsAppForwarding,
    data: data?.updateWhatsAppForwarding,
    loading,
    error,
  };
};

export const UPDATE_WHATSAPP_FORWARDING_MUTATION = gql`
  mutation UpdateWhatsAppForwarding(
    $whatsappForwardingId: String!
    $updatedFields: WhatsAppForwardingUpdateInput!
  ) {
    updateWhatsAppForwarding(
      whatsappForwardingId: $whatsappForwardingId
      updatedFields: $updatedFields
    ) {
      whatsappForwardingId
      phoneNumber
      name
      onboardingState
      createdAt
      updatedAt
    }
  }
`;
